// AUTO-GENERATED from weborder.json (OpenAPI). Do not edit by hand.
// Domain: insured
// Regenerate via scripts/gen-dtos.py

import { NotesDto } from "./files.dto.js";
import { GroupQuestionsDto, SubmissionQuestionAnswersSaveRequest } from "./questions.dto.js";

/**
 * Body for POST /api/weborder/v1/insured/submission. Creates a new submission
 * (order) for the authenticated insured. All fields are required; zipcode
 * must be 5 digits; speciality, zipcode, and coverage ILF/DLF must exist and
 * be valid for the active group.
 */
export class InsuredOrderRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // Practice or risk location ZIP code. Must be exactly 5 digits.
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Policy effective date in MM/dd/yyyy format.
    /** @type {number} */
    this.coverageilfdlfid = raw.coverageilfdlfid ?? 0; // Coverage ILF/DLF identifier. Must be greater than 0.
    /** @type {string} */
    this.hoursperweek = raw.hoursperweek ?? ""; // Hours worked label resolved on the client from the HoursWorkedFactorLookup selection (e.g....
    /** @type {number} */
    this.parttimeFulltimeFactor = raw.parttimeFulltimeFactor ?? 0; // Part-time/full-time factor resolved on the client from the HoursWorkedFactorLookup selecti...
    /** @type {number} */
    this.year = raw.year ?? 0; // Policy year. Example: 1
    /** @type {number} */
    this.speciality = raw.speciality ?? 0; // Speciality identifier. Must be greater than 0.
    /** @type {number} */
    this.claims = raw.claims ?? 0; // Number of claims associated. Can be 0 or more.
    /** @type {SubmissionQuestionAnswersSaveRequest | null} */
    this.questionSaveRequest = raw.questionSaveRequest ? new SubmissionQuestionAnswersSaveRequest(raw.questionSaveRequest) : null;
  }
}

/**
 * SSP web-order signup payload. **Insured vs contact name:** `firstName` and
 * `lastName` are always the **primary contact** name. When `isEntity` is
 * `true`, the **insured** (account) name is `companyName` (required). When
 * `isEntity` is `false`, the insured is a person and the insured name is also
 * `firstName` + `lastName`. **`title`** is the primary **location** title
 * (also used for contact display name). **`clientUrl`** is optional; when
 * sent it must match an existing broker client URL, otherwise the API returns
 * 404. When omitted, the default broker from server configuration is used.
 * **Payment term** for the new insured is set from server configuration (not
 * supplied in this request). **`submissionRequest`** is optional: omit or
 * null to skip creating a submission at signup.
 */
export class InsuredRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {boolean} */
    this.isEntity = raw.isEntity ?? false; // When true, insured is a business/entity — `companyName` is the **insured** name (required)...
    /** @type {string} */
    this.firstName = raw.firstName ?? ""; // Primary contact given name. Always required. For a person insured (`isEntity` false), this...
    /** @type {string} */
    this.lastName = raw.lastName ?? ""; // Primary contact family name. Always required. For a person insured (`isEntity` false), thi...
    /** @type {string} */
    this.companyName = raw.companyName ?? ""; // Entity insured legal or practice name. **Required** when `isEntity` is `true`. Not used wh...
    /** @type {string} */
    this.zipCode = raw.zipCode ?? ""; // Primary location ZIP code (US-style, 5 characters).
    /** @type {string} */
    this.address1 = raw.address1 ?? ""; // Primary location address line 1.
    /** @type {string} */
    this.address2 = raw.address2 ?? ""; // Primary location address line 2 (optional).
    /** @type {string} */
    this.city = raw.city ?? ""; // Primary location city.
    /** @type {string} */
    this.state = raw.state ?? ""; // Primary location state (code or name per client convention).
    /** @type {string} */
    this.email = raw.email ?? ""; // Login username; also used as unique account email.
    /** @type {string} */
    this.contactNumber = raw.contactNumber ?? ""; // Primary phone number for the insured contact.
    /** @type {string} */
    this.password = raw.password ?? ""; // Initial account password (stored hashed).
    /** @type {string} */
    this.title = raw.title ?? ""; // Title for the **primary location** (display name); also applied as the primary contact nam...
    /** @type {InsuredOrderRequest | null} */
    this.submissionRequest = raw.submissionRequest ? new InsuredOrderRequest(raw.submissionRequest) : null;
    /** @type {string} */
    this.clientUrl = raw.clientUrl ?? ""; // Optional broker **client URL / token** used to resolve the assigning broker. When omitted,...
    /** @type {string} */
    this.licenseNumber = raw.licenseNumber ?? ""; // Medical license number (optional).
    /** @type {string} */
    this.npiNumber = raw.npiNumber ?? "";
  }
}

export class InsuredDataResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.username = raw.username ?? "";
    /** @type {string} */
    this.email = raw.email ?? "";
    /** @type {string} */
    this.abbreviation = raw.abbreviation ?? "";
  }
}

/**
 * Update authenticated insured profile (`PUT /api/weborder/v1/insured`).
 * `insuredtype` **Entity** or **person** (case-insensitive). **404** if
 * insured or payment term missing; **406** if email duplicate. Optional
 * **licenseNumber** (alphanumeric, max 40) and **npiNumber** (10 digits).
 */
export class InsureUpdatedRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.firstname = raw.firstname ?? ""; // First name (person) or contact first name.
    /** @type {string} */
    this.lastname = raw.lastname ?? ""; // Last name (person) or contact last name.
    /** @type {string} */
    this.companyname = raw.companyname ?? ""; // Company name when `insuredtype` is **Entity**.
    /** @type {string} */
    this.insuredtype = raw.insuredtype ?? ""; // **Entity** or **person** (case-insensitive).
    /** @type {string} */
    this.gender = raw.gender ?? ""; // Optional gender code.
    /** @type {string} */
    this.dob = raw.dob ?? ""; // Date of birth **MM/dd/yyyy**. Future dates are not allowed.
    /** @type {string} */
    this.industrycode = raw.industrycode ?? ""; // Six-digit industry code (optional).
    /** @type {number} */
    this.paymentterm = raw.paymentterm ?? 0; // Payment term lookup id.
    /** @type {string} */
    this.email = raw.email ?? ""; // Account email (username).
    /** @type {string} */
    this.password = raw.password ?? ""; // New password when changing (optional).
    /** @type {string} */
    this.licenseNumber = raw.licenseNumber ?? ""; // Medical license number (optional).
    /** @type {string} */
    this.npiNumber = raw.npiNumber ?? ""; // National Provider Identifier, 10 digits when provided (optional).
  }
}

/**
 * Update insured plus plan location/contact on a submission (`PUT
 * /api/weborder/v1/insured/with-location`). **Entity** vs **person** rules
 * match `insuredtype`; `locationId` / `contactId` must exist on
 * `submissionId`.
 */
export class InsureWithLocationUpdatedRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.firstname = raw.firstname ?? ""; // Contact first name (person) or contact given name (entity).
    /** @type {string} */
    this.lastname = raw.lastname ?? ""; // Contact last name (person) or contact family name (entity).
    /** @type {string} */
    this.companyname = raw.companyname ?? ""; // Company name when `insuredtype` is **Entity**.
    /** @type {string} */
    this.insuredtype = raw.insuredtype ?? ""; // **Entity** or **person** (case-insensitive).
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? ""; // Phone (10–15 characters).
    /** @type {string} */
    this.address1 = raw.address1 ?? ""; // Plan location address line 1.
    /** @type {string} */
    this.address2 = raw.address2 ?? ""; // Address line 2 (optional).
    /** @type {string} */
    this.insureddob = raw.insureddob ?? ""; // Date of birth **MM/dd/yyyy** for person insured snapshots. Future dates are not allowed.
    /** @type {string} */
    this.licenseNumber = raw.licenseNumber ?? ""; // Medical license number (optional).
    /** @type {string} */
    this.npiNumber = raw.npiNumber ?? ""; // National Provider Identifier, 10 digits when provided (optional).
    /** @type {string} */
    this.state = raw.state ?? ""; // State.
    /** @type {string} */
    this.city = raw.city ?? ""; // City.
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // 5-digit ZIP.
    /** @type {boolean} */
    this.isupdatemaster = raw.isupdatemaster ?? false; // Update insured master from body when true.
    /** @type {number} */
    this.locationId = raw.locationId ?? 0; // Plan location id on the submission.
    /** @type {number} */
    this.contactId = raw.contactId ?? 0; // Plan contact id on the submission.
    /** @type {number} */
    this.submissionId = raw.submissionId ?? 0; // Submission id.
    /** @type {boolean} */
    this.locationcategory = raw.locationcategory ?? false; // Location category flag.
  }
}

export class InsuredContactMasterResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.firstname = raw.firstname ?? "";
    /** @type {string} */
    this.lastname = raw.lastname ?? "";
    /** @type {string} */
    this.email = raw.email ?? "";
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? "";
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false;
    /** @type {string} */
    this.title = raw.title ?? "";
  }
}

/**
 * Insured **master** contact (`POST /api/weborder/v1/insured/contacts`). Omit
 * `id` or use **0** to create; set `id` for update (must belong to insured).
 */
export class ContactRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0; // Master contact id; omit or **0** for create.
    /** @type {string} */
    this.title = raw.title ?? ""; // Salutation or role title.
    /** @type {string} */
    this.firstname = raw.firstname ?? ""; // Given name.
    /** @type {string} */
    this.lastname = raw.lastname ?? ""; // Family name.
    /** @type {string} */
    this.email = raw.email ?? ""; // Email address.
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? ""; // Phone (10–15 characters).
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false; // Primary contact flag.
  }
}

export class ContactResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.firstname = raw.firstname ?? "";
    /** @type {string} */
    this.lastname = raw.lastname ?? "";
    /** @type {string} */
    this.email = raw.email ?? "";
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? "";
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false;
    /** @type {string} */
    this.title = raw.title ?? "";
  }
}

export class OrderContactResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.firstname = raw.firstname ?? "";
    /** @type {string} */
    this.lastname = raw.lastname ?? "";
    /** @type {string} */
    this.email = raw.email ?? "";
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? "";
  }
}

/**
 * Insured **master** location (`POST /api/weborder/v1/insured/locations`).
 * Omit `id` or use **0** to create; set `id` for update (must belong to
 * insured).
 */
export class LocationRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0; // Master location id; omit or **0** for create.
    /** @type {string} */
    this.title = raw.title ?? ""; // Location display title.
    /** @type {string} */
    this.address1 = raw.address1 ?? ""; // Address line 1.
    /** @type {string} */
    this.address2 = raw.address2 ?? ""; // Address line 2 (optional).
    /** @type {string} */
    this.city = raw.city ?? ""; // City.
    /** @type {string} */
    this.state = raw.state ?? ""; // State code or name.
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // 5-digit ZIP.
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false; // Whether this is the primary master location.
    /** @type {boolean} */
    this.locationcategory = raw.locationcategory ?? false; // Location category flag passed through to persistence.
  }
}

export class LocationListResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
    /** @type {string} */
    this.address1 = raw.address1 ?? "";
    /** @type {string} */
    this.address2 = raw.address2 ?? "";
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false;
  }
}

export class LocationMasterResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
    /** @type {string} */
    this.address1 = raw.address1 ?? "";
    /** @type {string} */
    this.address2 = raw.address2 ?? "";
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false;
    /** @type {boolean} */
    this.locationcategory = raw.locationcategory ?? false;
  }
}

export class OrderLocationResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.address1 = raw.address1 ?? "";
    /** @type {string} */
    this.address2 = raw.address2 ?? "";
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {number} */
    this.locationId = raw.locationId ?? 0;
    /** @type {string} */
    this.locationTitle = raw.locationTitle ?? "";
  }
}

/**
 * Add insured master locations to a submission as plan locations, optionally
 * removing existing plan locations first.
 */
export class AddLocationRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.submissionid = raw.submissionid ?? 0; // Submission id (must belong to the caller)
    /** @type {number[]} */
    this.addLocationsIds = Array.isArray(raw.addLocationsIds) ? raw.addLocationsIds : []; // Insured **master** location ids to attach as plan locations (each must exist for the insur...
    /** @type {number[]} */
    this.removeLocationsIds = Array.isArray(raw.removeLocationsIds) ? raw.removeLocationsIds : []; // Optional **plan** location ids to remove first (same rules as `DELETE .../locations/plan`)...
  }
}

/**
 * Update a **plan** (submission) location (`PUT
 * /api/weborder/v1/insured/locations/plan`). Submission must belong to the
 * authenticated insured.
 */
export class PlanLocationRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0; // Plan location row id.
    /** @type {number} */
    this.submissionId = raw.submissionId ?? 0; // Owning submission id.
    /** @type {string} */
    this.address1 = raw.address1 ?? ""; // Address line 1.
    /** @type {string} */
    this.address2 = raw.address2 ?? ""; // Address line 2 (optional).
    /** @type {string} */
    this.city = raw.city ?? ""; // City.
    /** @type {string} */
    this.state = raw.state ?? ""; // State (must exist in system).
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // 5-digit ZIP.
    /** @type {boolean} */
    this.isupdatemaster = raw.isupdatemaster ?? false; // When true, propagate changes to insured master location.
    /** @type {boolean} */
    this.locationcategory = raw.locationcategory ?? false; // Location category flag.
  }
}

export class PlanLocationsResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
    /** @type {string} */
    this.address1 = raw.address1 ?? "";
    /** @type {string} */
    this.address2 = raw.address2 ?? "";
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {number} */
    this.locationId = raw.locationId ?? 0;
    /** @type {boolean} */
    this.isprimary = raw.isprimary ?? false;
    /** @type {boolean} */
    this.isremoved = raw.isremoved ?? false;
    /** @type {boolean} */
    this.isapproved = raw.isapproved ?? false;
    /** @type {boolean} */
    this.locationcategory = raw.locationcategory ?? false;
  }
}

export class OrderDetailsResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.submissionId = raw.submissionId ?? 0;
    /** @type {number} */
    this.insuredId = raw.insuredId ?? 0;
    /** @type {boolean} */
    this.isentity = raw.isentity ?? false;
    /** @type {string} */
    this.companyname = raw.companyname ?? "";
    /** @type {string} */
    this.insuredfirstname = raw.insuredfirstname ?? "";
    /** @type {string} */
    this.insuredlastname = raw.insuredlastname ?? "";
    /** @type {string} */
    this.insureddob = raw.insureddob ?? "";
    /** @type {string} */
    this.licenseNumber = raw.licenseNumber ?? "";
    /** @type {string} */
    this.npiNumber = raw.npiNumber ?? "";
    /** @type {string} */
    this.workflowstatus = raw.workflowstatus ?? "";
    /** @type {OrderRatingResponse | null} */
    this.ratingResponse = raw.ratingResponse ? new OrderRatingResponse(raw.ratingResponse) : null;
    /** @type {OrderContactResponse | null} */
    this.contactResponse = raw.contactResponse ? new OrderContactResponse(raw.contactResponse) : null;
    /** @type {OrderLocationResponse | null} */
    this.locationResponse = raw.locationResponse ? new OrderLocationResponse(raw.locationResponse) : null;
    /** @type {number} */
    this.year = raw.year ?? 0;
    /** @type {ExclusionResponse[]} */
    this.exclusionList = Array.isArray(raw.exclusionList) ? raw.exclusionList.map((x) => new ExclusionResponse(x)) : [];
    /** @type {RequiredItemResponse[]} */
    this.requiredItemList = Array.isArray(raw.requiredItemList) ? raw.requiredItemList.map((x) => new RequiredItemResponse(x)) : [];
    /** @type {NotesDto[]} */
    this.noteList = Array.isArray(raw.noteList) ? raw.noteList.map((x) => new NotesDto(x)) : [];
    /** @type {PlanLocationsResponse[]} */
    this.planLocatoins = Array.isArray(raw.planLocatoins) ? raw.planLocatoins.map((x) => new PlanLocationsResponse(x)) : [];
    /** @type {GroupQuestionsDto[]} */
    this.groupQuestionsDto = Array.isArray(raw.groupQuestionsDto) ? raw.groupQuestionsDto.map((x) => new GroupQuestionsDto(x)) : [];
    /** @type {number} */
    this.questionsAttemptedTotal = raw.questionsAttemptedTotal ?? 0;
    /** @type {number} */
    this.questionsGrandTotal = raw.questionsGrandTotal ?? 0;
    /** @type {boolean} */
    this.iswriteaccess = raw.iswriteaccess ?? false;
    /** @type {boolean} */
    this.ispolicyactive = raw.ispolicyactive ?? false;
    /** @type {number} */
    this.totalClaims = raw.totalClaims ?? 0;
    /** @type {string} */
    this.quotenumber = raw.quotenumber ?? "";
    /** @type {string} */
    this.policynumber = raw.policynumber ?? "";
    /** @type {string} */
    this.policyStatus = raw.policyStatus ?? "";
    /** @type {boolean} */
    this.questionRequired = raw.questionRequired ?? false;
  }
}

export class OrderSummaryDetailsResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {boolean} */
    this.isentity = raw.isentity ?? false;
    /** @type {string} */
    this.companyname = raw.companyname ?? "";
    /** @type {string} */
    this.insuredfirstname = raw.insuredfirstname ?? "";
    /** @type {string} */
    this.insuredlastname = raw.insuredlastname ?? "";
    /** @type {string} */
    this.contactnumber = raw.contactnumber ?? "";
    /** @type {string} */
    this.address1 = raw.address1 ?? "";
    /** @type {string} */
    this.address2 = raw.address2 ?? "";
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {OpenOrderDetailsDto | null} */
    this.orderDetailsResponse = raw.orderDetailsResponse ? new OpenOrderDetailsDto(raw.orderDetailsResponse) : null;
    /** @type {InvoiceHeaderResponse | null} */
    this.invoiceHeaderResponse = raw.invoiceHeaderResponse ? new InvoiceHeaderResponse(raw.invoiceHeaderResponse) : null;
    /** @type {string[]} */
    this.coverageEndorsements = Array.isArray(raw.coverageEndorsements) ? raw.coverageEndorsements : [];
    /** @type {string} */
    this.workflowStatus = raw.workflowStatus ?? "";
  }
}

export class OrderRatingResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.practicezipcode = raw.practicezipcode ?? "";
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? "";
    /** @type {string} */
    this.expirationDate = raw.expirationDate ?? "";
    /** @type {string} */
    this.retroDate = raw.retroDate ?? "";
    /** @type {number} */
    this.coverageLimitId = raw.coverageLimitId ?? 0;
    /** @type {number} */
    this.retroLimitId = raw.retroLimitId ?? 0;
    /** @type {string} */
    this.coverageLimitTitle = raw.coverageLimitTitle ?? "";
    /** @type {string} */
    this.retroLimitTitle = raw.retroLimitTitle ?? "";
    /** @type {number} */
    this.premium = raw.premium ?? 0;
    /** @type {number} */
    this.tax = raw.tax ?? 0;
    /** @type {number} */
    this.fees = raw.fees ?? 0;
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {number} */
    this.parttimeFulltimeFactor = raw.parttimeFulltimeFactor ?? 0;
    /** @type {number} */
    this.specialtiesid = raw.specialtiesid ?? 0;
    /** @type {string} */
    this.hoursPerWeekLookName = raw.hoursPerWeekLookName ?? "";
    /** @type {number} */
    this.surgeryId = raw.surgeryId ?? 0;
    /** @type {string} */
    this.surgeryName = raw.surgeryName ?? "";
    /** @type {string} */
    this.specialtyTitle = raw.specialtyTitle ?? "";
    /** @type {string} */
    this.practiceLocation = raw.practiceLocation ?? "";
    /** @type {string} */
    this.practiceCity = raw.practiceCity ?? "";
  }
}

export class OpenOrderDetailsDto {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {number} */
    this.submission = raw.submission ?? 0;
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? "";
    /** @type {string} */
    this.retroDate = raw.retroDate ?? "";
    /** @type {string} */
    this.coverageLimitTitle = raw.coverageLimitTitle ?? "";
    /** @type {string} */
    this.retrolimitTitle = raw.retrolimitTitle ?? "";
    /** @type {number} */
    this.premium = raw.premium ?? 0;
    /** @type {number} */
    this.taxtotal = raw.taxtotal ?? 0;
    /** @type {number} */
    this.feestotal = raw.feestotal ?? 0;
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {boolean} */
    this.ratingPolicyBound = raw.ratingPolicyBound ?? false;
    /** @type {string} */
    this.expiredDate = raw.expiredDate ?? "";
    /** @type {string} */
    this.invoiceNumber = raw.invoiceNumber ?? "";
    /** @type {string} */
    this.practicezipcode = raw.practicezipcode ?? "";
    /** @type {string} */
    this.hoursPerWeek = raw.hoursPerWeek ?? "";
    /** @type {string} */
    this.workFlowStatus = raw.workFlowStatus ?? "";
    /** @type {string} */
    this.specialityTitle = raw.specialityTitle ?? "";
    /** @type {number} */
    this.specialitiesMasterId = raw.specialitiesMasterId ?? 0;
  }
}

export class OpenOrdersResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.submissionid = raw.submissionid ?? 0;
    /** @type {string} */
    this.speciality = raw.speciality ?? "";
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? "";
    /** @type {string} */
    this.quotenumber = raw.quotenumber ?? "";
  }
}

export class SubmissionDetails {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.submissionid = raw.submissionid ?? 0;
    /** @type {string} */
    this.policynumber = raw.policynumber ?? "";
    /** @type {string} */
    this.effectivedate = raw.effectivedate ?? "";
    /** @type {string} */
    this.expireddate = raw.expireddate ?? "";
    /** @type {string} */
    this.retrodate = raw.retrodate ?? raw.retroDate ?? raw.retroactivedate ?? "";
    /** @type {number} */
    this.balance = raw.balance ?? 0;
    /** @type {string} */
    this.statusname = raw.statusname ?? "";
    /** @type {string} */
    this.policystatus = raw.policystatus ?? "";
    /** @type {boolean} */
    this.isopenorder = raw.isopenorder ?? false;
    /** @type {boolean} */
    this.ispolicyactive = raw.ispolicyactive ?? false;
    /** @type {string} */
    this.practicezipcode = raw.practicezipcode ?? "";
    /** @type {string} */
    this.filterstatus = raw.filterstatus ?? "";
    /** @type {string} */
    this.speciality = raw.speciality ?? "";
    /** @type {string} */
    this.insuredName = raw.insuredName ?? "";
    /** @type {string} */
    this.quotenumber = raw.quotenumber ?? "";
  }
}

export class PolicyInfoResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.policyNumber = raw.policyNumber ?? "";
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? "";
    /** @type {string} */
    this.expirationDate = raw.expirationDate ?? "";
    /** @type {string} */
    this.useremail = raw.useremail ?? "";
    /** @type {number} */
    this.amount = raw.amount ?? 0;
    /** @type {string} */
    this.insuredName = raw.insuredName ?? "";
  }
}

export class CoverageClaimsResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
    /** @type {string} */
    this.type = raw.type ?? "";
    /** @type {string} */
    this.incidentdate = raw.incidentdate ?? "";
    /** @type {string} */
    this.filingdate = raw.filingdate ?? "";
    /** @type {number} */
    this.monthstofile = raw.monthstofile ?? 0;
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {string} */
    this.status = raw.status ?? "";
    /** @type {boolean} */
    this.openstatus = raw.openstatus ?? false;
  }
}

export class ExclusionResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
  }
}

export class RequiredItemResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
    /** @type {boolean} */
    this.received = raw.received ?? false;
    /** @type {string} */
    this.status = raw.status ?? "";
  }
}

export class PrimaryInsuredDataDto {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.firstname = raw.firstname ?? "";
    /** @type {string} */
    this.lastname = raw.lastname ?? "";
    /** @type {string} */
    this.companyname = raw.companyname ?? "";
    /** @type {string} */
    this.insuredtype = raw.insuredtype ?? "";
    /** @type {string} */
    this.gender = raw.gender ?? "";
    /** @type {string} */
    this.dob = raw.dob ?? "";
    /** @type {string} */
    this.industrycode = raw.industrycode ?? "";
    /** @type {number} */
    this.paymenttermid = raw.paymenttermid ?? 0;
    /** @type {string} */
    this.email = raw.email ?? "";
    /** @type {string} */
    this.licenseNumber = raw.licenseNumber ?? "";
    /** @type {string} */
    this.npiNumber = raw.npiNumber ?? "";
  }
}

export class PaymentTermResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.title = raw.title ?? "";
  }
}

export class InvoiceHeaderResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.policynumber = raw.policynumber ?? "";
    /** @type {string} */
    this.invoicedate = raw.invoicedate ?? "";
    /** @type {string} */
    this.effectivedate = raw.effectivedate ?? "";
    /** @type {number} */
    this.paymentDays = raw.paymentDays ?? 0;
    /** @type {number} */
    this.invoiceamount = raw.invoiceamount ?? 0;
    /** @type {number} */
    this.balance = raw.balance ?? 0;
    /** @type {number} */
    this.balanceDue = raw.balanceDue ?? 0;
    /** @type {number} */
    this.appliedAmount = raw.appliedAmount ?? 0;
    /** @type {string} */
    this.invoiceStatus = raw.invoiceStatus ?? "";
    /** @type {number} */
    this.countAppliedAmount = raw.countAppliedAmount ?? 0;
    /** @type {number} */
    this.totalPremium = raw.totalPremium ?? 0;
    /** @type {number} */
    this.totalFees = raw.totalFees ?? 0;
    /** @type {number} */
    this.totalTaxes = raw.totalTaxes ?? 0;
    /** @type {number} */
    this.totalCommission = raw.totalCommission ?? 0;
    /** @type {string} */
    this.paymentduedate = raw.paymentduedate ?? "";
    /** @type {boolean} */
    this.voided = raw.voided ?? false;
  }
}

export class SendToCloseResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.policystatus = raw.policystatus ?? "";
    /** @type {string} */
    this.statusname = raw.statusname ?? "";
  }
}

export class DocuSignEmbeddedSigningResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.signingUrl = raw.signingUrl ?? ""; // DocuSign embedded signing ceremony URL
    /** @type {string} */
    this.envelopeId = raw.envelopeId ?? ""; // DocuSign envelope id
    /** @type {boolean} */
    this.signedCompleted = raw.signedCompleted ?? false;
  }
}
